package macha.sh.kotlin.components.widgets

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Div
import macha.sh.kotlin.SiteColors

// Local, non-fullscreen markdown style used by widgets.
val MarkdownContentStyle = CssStyle {
    base { Modifier.fillMaxWidth() }

    cssRule("h1") { Modifier.fontSize(3.cssRem).fontWeight(400).margin(bottom = 2.cssRem) }
    cssRule("h2") { Modifier.fontSize(2.4.cssRem).fontWeight(300).margin(topBottom = 1.5.cssRem) }
    cssRule("h3") { Modifier.fontSize(1.8.cssRem).fontWeight(300).margin(topBottom = 1.25.cssRem) }
    cssRule("h4") { Modifier.fontSize(1.2.cssRem).fontWeight(600).margin(top = 1.cssRem, bottom = 0.5.cssRem) }

    cssRule("pre") { Modifier.margin(top = 0.5.cssRem, bottom = 2.cssRem).fillMaxWidth() }
    cssRule("pre > code") {
        Modifier
            .display(DisplayStyle.Block)
            .fillMaxWidth()
            .backgroundColor(SiteColors.SiteBkg)
            .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SiteColors.Border)
            .borderRadius(0.25.cssRem)
            .padding(0.5.cssRem)
            .fontSize(1.cssRem)
            .overflow { x(com.varabyte.kobweb.compose.css.Overflow.Auto) }
    }

    cssRule("ul, ol") { Modifier.padding(left = 1.25.cssRem) }
    cssRule("li") { Modifier.margin(bottom = 0.25.cssRem) }
    cssRule("a") { Modifier.color(SiteColors.Hover) }
    cssRule("img") { Modifier.maxWidth(100.percent) }
}

@Composable
fun MarkdownBox(text: String, modifier: Modifier = Modifier) {
    Div(MarkdownContentStyle.toModifier().then(modifier).toAttrs()) { SimpleMarkdown(text) }
}

/**
 * Convenience container that loads a Markdown file from a public resource path (e.g. "/content/about.md")
 * and renders it styled. Shows light error/loading messages while fetching.
 */
@Composable
fun MarkdownFileBox(resourcePath: String, modifier: Modifier = Modifier) {
    var content by remember(resourcePath) { mutableStateOf<String?>(null) }
    var error by remember(resourcePath) { mutableStateOf<String?>(null) }

    LaunchedEffect(resourcePath) {
        try {
            val response = window.fetch(resourcePath).await()
            if (response.ok) {
                content = response.text().await()
                error = null
            } else {
                content = null
                error = "Could not load content (HTTP ${'$'}{response.status})."
            }
        } catch (t: Throwable) {
            content = null
            error = "Error loading content: ${'$'}{t.message}"
        }
    }

    val body = content ?: error ?: "Loading content…"
    MarkdownBox(text = body, modifier = modifier)
}

@Composable
fun MarkdownSection(text: String, modifier: Modifier = Modifier) {
    MarkdownBox(text = text, modifier = modifier)
}
